import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { UserPlus, KeyRound, Users, Download, Pencil } from "lucide-react";
import { toast } from "sonner";
import { exportCsv } from "@/lib/exportCsv";

const ROLES = ["participant", "doctor", "user_manager", "admin"];

export default function UsersAdminPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "participant", participant_code: "" });
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [editFor, setEditFor] = useState(null);
  const [editName, setEditName] = useState("");

  const isAdmin = user?.role === "admin";
  const roleOptions = isAdmin ? ROLES : ROLES.filter((r) => r !== "admin");

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setShowNew(false); setForm({ name: "", email: "", password: "", role: "participant", participant_code: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const savePw = async () => {
    try { await api.patch(`/users/${pwFor.id}/password`, { password: newPw }); toast.success("Password updated"); setPwFor(null); setNewPw(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (u) => { setEditFor(u); setEditName(u.name || ""); };

  const saveName = async () => {
    const name = editName.trim();
    if (!name) { toast.error("Name cannot be empty"); return; }
    try {
      await api.patch(`/users/${editFor.id}`, { name });
      toast.success("Name updated");
      setEditFor(null); setEditName("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doctors = users.filter((u) => u.role === "doctor");

  const exportUsers = () => {
    if (!users || users.length === 0) { toast.error("Nothing to export"); return; }
    const docName = (id) => doctors.find((d) => d.id === id)?.name || "";
    const rows = users.map((u) => ({
      name: u.name,
      email: u.email,
      role: u.role,
      code: u.participant_code || "",
      doctor: (u.assigned_doctor_ids || []).map(docName).filter(Boolean).join("; "),
    }));
    const columns = [
      { label: "Name", key: "name" },
      { label: "Email", key: "email" },
      { label: "Role", key: "role" },
      { label: "Participant code", key: "code" },
      { label: "Assigned doctor", key: "doctor" },
    ];
    exportCsv(`Users - ${new Date().toISOString().slice(0, 10)}`, columns, rows);
  };

  const assign = async (u, docId) => {
    const ids = docId ? [docId] : [];
    try { await api.patch(`/users/${u.id}`, { assigned_doctor_ids: ids }); toast.success("Assignment updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-serif text-3xl font-bold text-stone-900"><Users className="h-7 w-7 text-emerald-700" /> {t("User Management")}</h1>
          <p className="text-sm text-stone-500">{t("Create accounts and manage passwords.")} {isAdmin && t("Assign doctors to participants.")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportUsers} data-testid="export-users-btn"
            disabled={!users || users.length === 0}
            className="flex items-center gap-2 rounded-md border border-[#E5DFD3] bg-white px-4 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40">
            <Download className="h-4 w-4" /> {t("Export")}
          </button>
          <button onClick={() => setShowNew(true)} data-testid="add-user-btn"
            className="flex items-center gap-2 rounded-md bg-emerald-800 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-900">
            <UserPlus className="h-4 w-4" /> {t("New User")}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-[#E5DFD3] bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-[#E8EFE9] text-left">
            <tr>
              <th className="w-12 px-4 py-2 font-mono text-xs uppercase tracking-wider text-stone-500">#</th>
              {["Name", "Email", "Role", isAdmin ? "Assigned doctor" : "Code", "Actions"].map((h) => (
                <th key={h} className="px-4 py-2 font-mono text-xs uppercase tracking-wider text-stone-500">{t(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.id} className="border-t border-[#EFE9DE]" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-2 text-stone-500 tabular-nums">{i + 1}</td>
                <td className="px-4 py-2 font-medium text-stone-800">{u.name}</td>
                <td className="px-4 py-2 text-stone-600">{u.email}</td>
                <td className="px-4 py-2"><span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs">{t(u.role)}</span></td>
                <td className="px-4 py-2 text-stone-600">
                  {isAdmin && (u.role === "participant" || u.role === "admin") ? (
                    <select value={u.assigned_doctor_ids?.[0] || ""} onChange={(e) => assign(u, e.target.value)}
                      data-testid={`assign-${u.id}`} className="rounded-md border border-[#E5DFD3] px-2 py-1 text-xs">
                      <option value="">{t("Unassigned")}</option>
                      {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  ) : (u.participant_code || "—")}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <button onClick={() => openEdit(u)} data-testid={`edit-${u.id}`}
                        className="flex items-center gap-1 rounded-md border border-[#E5DFD3] px-2 py-1 text-xs text-stone-600 hover:bg-stone-50">
                        <Pencil className="h-3 w-3" /> {t("Edit")}
                      </button>
                    )}
                    <button onClick={() => setPwFor(u)} data-testid={`pw-${u.id}`}
                      className="flex items-center gap-1 rounded-md border border-[#E5DFD3] px-2 py-1 text-xs text-stone-600 hover:bg-stone-50">
                      <KeyRound className="h-3 w-3" /> {t("Password")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <Modal title={t("Create User")} onClose={() => setShowNew(false)}>
          {["name", "email", "password"].map((k) => (
            <div key={k} className="mb-3">
              <label className="mb-1 block text-sm font-medium capitalize text-stone-700">{t(k === "name" ? "Name" : k === "email" ? "Email" : "Password")}</label>
              <input type={k === "password" ? "text" : k === "email" ? "email" : "text"} value={form[k]}
                data-testid={`user-${k}`} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full rounded-md border border-[#E5DFD3] px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none" />
            </div>
          ))}
          <div className="mb-3">
            <label className="mb-1 block text-sm font-medium text-stone-700">{t("Role")}</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role"
              className="w-full rounded-md border border-[#E5DFD3] px-3 py-2 text-sm">
              {roleOptions.map((r) => <option key={r} value={r}>{t(r)}</option>)}
            </select>
          </div>
          <div className="mb-3">
            <label className="mb-1 block text-sm font-medium text-stone-700">{t("Participant code (optional)")}</label>
            <input value={form.participant_code} onChange={(e) => setForm({ ...form, participant_code: e.target.value })}
              className="w-full rounded-md border border-[#E5DFD3] px-3 py-2 text-sm" />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setShowNew(false)} className="rounded-md border border-[#E5DFD3] px-4 py-2 text-sm">{t("Cancel")}</button>
            <button onClick={create} data-testid="user-create" className="rounded-md bg-emerald-800 px-4 py-2 text-sm font-semibold text-white">{t("Create")}</button>
          </div>
        </Modal>
      )}

      {editFor && (
        <Modal title={`${t("Edit user")} — ${editFor.name}`} onClose={() => setEditFor(null)}>
          <label className="mb-1 block text-sm font-medium text-stone-700">{t("Email")}</label>
          <input value={editFor.email} disabled data-testid="edit-email"
            className="w-full cursor-not-allowed rounded-md border border-[#E5DFD3] bg-stone-100 px-3 py-2 text-sm text-stone-500" />
          <p className="mt-1 mb-3 text-xs text-stone-400">{t("Email cannot be changed.")}</p>
          <label className="mb-1 block text-sm font-medium text-stone-700">{t("Name")}</label>
          <input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-name"
            className="w-full rounded-md border border-[#E5DFD3] px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none" />
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setEditFor(null)} className="rounded-md border border-[#E5DFD3] px-4 py-2 text-sm">{t("Cancel")}</button>
            <button onClick={saveName} data-testid="save-name" className="rounded-md bg-emerald-800 px-4 py-2 text-sm font-semibold text-white">{t("Update")}</button>
          </div>
        </Modal>
      )}

      {pwFor && (
        <Modal title={`${t("New password")} — ${pwFor.name}`} onClose={() => setPwFor(null)}>
          <label className="mb-1 block text-sm font-medium text-stone-700">{t("New password")}</label>
          <input value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password"
            className="w-full rounded-md border border-[#E5DFD3] px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none" />
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setPwFor(null)} className="rounded-md border border-[#E5DFD3] px-4 py-2 text-sm">{t("Cancel")}</button>
            <button onClick={savePw} data-testid="save-password" className="rounded-md bg-emerald-800 px-4 py-2 text-sm font-semibold text-white">{t("Update")}</button>
          </div>
        </Modal>
      )}
    </AppShell>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="user-modal">
      <div className="w-full max-w-md rounded-lg border border-[#E5DFD3] bg-white p-6 shadow-xl">
        <h3 className="mb-2 font-serif text-xl font-semibold text-stone-800">{title}</h3>
        {children}
      </div>
    </div>
  );
}
